import {useState, useEffect} from 'react'
import {useFormik} from 'formik'
import * as Yup from 'yup'
import clsx from 'clsx'
import {registerWithEmailAndPassword} from '../core/_requests'
import {Link} from 'react-router-dom'
import {updateProfile} from 'firebase/auth'
import {PasswordMeterComponent} from '../../../../_metronic/assets/ts/components'
import {useAuth} from '../core/Auth'
import {v4 as uuid} from 'uuid'
import {Helmet} from 'react-helmet'
import posthog from 'posthog-js'

import {
  collection,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  setDoc,
  writeBatch,
} from '@firebase/firestore'
import db from '../../../../services/firebase'
import {toAbsoluteUrl} from '_metronic/helpers'
import {restrictedDomains} from '../core/restrictedDomain'

const initialValues = {
  firstname: '',
  lastname: '',
  email: '',
  password: '',
  acceptTerms: true,
}

const checkPasswordStrength = (password: string) => {
  const checks = {
    length: password.length >= 8,
    letters: /[a-zA-Z]/.test(password),
    numbers: /[0-9]/.test(password),
    symbols: /[^a-zA-Z0-9]/.test(password),
  }

  return {
    valid: Object.values(checks).every(Boolean),
  }
}

const registrationSchema = Yup.object().shape({
  firstname: Yup.string()
    .min(3, 'Minimum 3 characters')
    .max(50, 'Maximum 50 characters')
    .required('First name is required'),
  email: Yup.string()
    .email('Wrong email format')
    .min(3, 'Minimum 3 characters')
    .max(50, 'Maximum 50 characters')
    .required('Email is required')
    .test('is-work-email', 'Please use your company work email', (value) => {
      if (!value) return false
      const domain = value.split('@')[1]?.toLowerCase()
      if (!domain) return false
      return !restrictedDomains.includes(domain)
    }),
  lastname: Yup.string()
    .min(3, 'Minimum 3 characters')
    .max(50, 'Maximum 50 characters')
    .required('Last name is required'),
  password: Yup.string()
    .required('Password is required')
    .test(
      'password-strength',
      'Password must contain at least 8 characters with letters, numbers, and symbols',
      (value) => {
        if (!value) return false
        const result = checkPasswordStrength(value)
        return result.valid
      }
    ),
  acceptTerms: Yup.bool().required('You must accept the terms and conditions'),
})

const generateUniqueTenantId = () => {
  return uuid()
}

export function TrustCenterRegistration() {
  const [loading, setLoading] = useState(false)
  const {saveAuth, setCurrentUser} = useAuth()
  const [usersData, setUsersData] = useState<any[]>([])
  const [currentStep, setCurrentStep] = useState(1)

  useEffect(() => {
    async function fetchUsersData() {
      const usersCollectionRef = collection(db, 'users')
      try {
        const usersSnapshot = await getDocs(usersCollectionRef)
        const usersList = usersSnapshot.docs.map((doc) => doc.data())
        setUsersData(usersList)
      } catch (error) {
        console.error('Error fetching users data:', error)
      }
    }

    fetchUsersData()
  }, [])

  const formik = useFormik({
    initialValues,
    validationSchema: registrationSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      setLoading(true)
      try {
        const db = getFirestore()

        // Step 1: Register the new user
        const authCredential = await registerWithEmailAndPassword(
          values.email,
          values.password,
          values.firstname,
          values.lastname,
          '' // empty string for confirmPassword
        )

        if (!authCredential.user || !authCredential.user.uid) {
          throw new Error('User is not authenticated')
        }

        // Check if email is already in use
        const isEmailUsed = usersData.some((user) => user.email === values.email)
        if (isEmailUsed) {
          setStatus('Email is already in use.')
          setSubmitting(false)
          setLoading(false)
          return
        }

        const userDocRef = doc(db, 'users', authCredential.user.uid)
        const userDocSnap = await getDoc(userDocRef)

        let userModel

        if (!userDocSnap.exists()) {
          const userToken = await authCredential.user.getIdToken()
          const tenantId = generateUniqueTenantId()

          // Step 4: Update profile with first and last name
          await updateProfile(authCredential.user, {
            displayName: `${values.firstname} ${values.lastname}`,
          })

          // Step 5: Set up auth and user models
          const authModel = {
            api_token: userToken,
            email: authCredential.user.email,
            uid: authCredential.user.uid,
            userName: `${values.firstname} ${values.lastname}`,
            password: values.password,
            first_name: values.firstname,
            last_name: values.lastname,
            tenantId: tenantId,
          }

          userModel = {
            uid: authModel.uid,
            userName: authModel.userName,
            api_token: authModel.api_token,
            password: authModel.password,
            email: authModel.email,
            first_name: authModel.first_name,
            last_name: authModel.last_name,
            tenantId: tenantId,
            role: 'Owner',
            account_type: 'trust',
          }

          // Step 6: Upload user model to Firestore
          await setDoc(doc(db, 'users', authCredential.user.uid), userModel)

          // Save auth model and set current user
          saveAuth(authModel)
          setCurrentUser(userModel)

          // Create a batch write for setting up tenant data
          const formattedDate = new Date().toISOString().split('T')[0]
          const batch = writeBatch(db)

          // Step 7: Setup tenant data
          const tenantRef = doc(db, 'tenants', tenantId)
          batch.set(tenantRef, {})

          const controlsSnapshot = await getDocs(collection(db, 'default_controls'))
          controlsSnapshot.forEach((controlDoc) => {
            batch.set(doc(collection(tenantRef, 'controls'), controlDoc.id), controlDoc.data())
          })
          // }

          const testsSnapshot = await getDocs(collection(db, 'default_tests'))
          testsSnapshot.forEach((testDoc) => {
            batch.set(doc(collection(tenantRef, 'tests'), testDoc.id), testDoc.data())
          })

          const policySnapshot = await getDocs(collection(db, 'policy_templates'))
          policySnapshot.forEach((policyDoc) => {
            batch.set(doc(collection(tenantRef, 'policies'), policyDoc.id), policyDoc.data())
          })

          // Add default group and personnel checklist
          const groupRef = doc(collection(tenantRef, 'personnel_group'))
          batch.set(groupRef, {
            groupName: '[Default]',
            checklist: '[Default]',
            createdOn: formattedDate,
            createdBy: authModel.userName,
            tenantId: tenantId,
          })

          const checklistRef = doc(collection(tenantRef, 'personnel_checklist'))
          batch.set(checklistRef, {
            checklist_name: '[Default]',
            policies: [],
            createdon: formattedDate,
            createdBy: authModel.userName,
            tenantId: tenantId,
          })

          const personnelRef = doc(collection(tenantRef, 'employees'), authModel.uid)
          batch.set(personnelRef, {
            created_at: new Date().toISOString(),
            tenantId: tenantId,
            uid: authModel.uid,
            full_name: authModel.userName,
            group: '[Default]',
            role: 'Owner',
            start_date: formattedDate,
            email: authModel.email,
            account_type: 'trust',
            first_name: authModel.first_name,
            last_name: authModel.last_name,
          })

          await batch.commit()
        } else {
          // User exists, retrieve existing user model
          userModel = userDocSnap.data()
          window.location.href = '/trust-center'
        }
      } catch (error) {
        console.error('Registration for trust center only failed:', error)
        saveAuth(undefined)
        setStatus('The registration details are incorrect')
      } finally {
        setSubmitting(false)
        setLoading(false)
      }
    },
  })

  useEffect(() => {
    PasswordMeterComponent.bootstrap()
  }, [])

  useEffect(() => {
    if (formik.status) {
      window.scrollTo({top: 0, behavior: 'smooth'})
    }
  }, [formik.status])

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className='d-flex flex-column justify-content-center'>
            <h1 className='text-dark fw-bolder mb-3'>Create your trust center for free</h1>

            {/* <p className='text-gray-700 fw-semibold fs-6'>
              No credit-card required to start. Access to premium features and integrations.
            </p> */}

            <div className='d-flex flex-column justify-content-center gap-4 mt-4'>
              {formik.status && (
                <div className='error-message alert alert-danger'>
                  <div className='alert-text font-weight-bold'>{formik.status}</div>
                </div>
              )}

              <div className=''>
                <label className='form-label fw-semibold text-dark fs-6'>Email</label>
                <input
                  placeholder='Email'
                  type='email'
                  autoComplete='off'
                  {...formik.getFieldProps('email')}
                  className={clsx(
                    'form-control form-control-sm bg-transparent',
                    {'is-invalid': formik.touched.email && formik.errors.email},
                    {
                      'is-valid': formik.touched.email && !formik.errors.email,
                    }
                  )}
                />
                {formik.touched.email && formik.errors.email && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.email}</span>
                    </div>
                  </div>
                )}
              </div>

              <div className=''>
                <span className='fs-7'>
                  By clicking "Continue" you agree to SecureSlate's
                  <a
                    href='https://getsecureslate.com/policy#terms'
                    target='_blank'
                    className='ms-1 link-primary fw-semibold'
                  >
                    Terms & Conditions
                  </a>{' '}
                  and
                  <a
                    href='https://getsecureslate.com/policy#privacy'
                    target='_blank'
                    className='ms-1 link-primary fw-semibold'
                  >
                    Privacy Policy
                  </a>
                  .
                </span>
              </div>
            </div>
          </div>
        )
      case 2:
        return (
          <div className='' data-kt-password-meter='true'>
            <label className='form-label fw-semibold text-dark fs-6'>Password</label>
            <div className='position-relative mb-3'>
              <input
                type='password'
                placeholder='Password'
                autoComplete='off'
                {...formik.getFieldProps('password')}
                className={clsx(
                  'form-control form-control-sm bg-transparent',
                  {
                    'is-invalid': formik.touched.password && formik.errors.password,
                  },
                  {
                    'is-valid': formik.touched.password && !formik.errors.password,
                  }
                )}
              />
              {formik.touched.password && formik.errors.password && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{formik.errors.password}</span>
                  </div>
                </div>
              )}
            </div>

            <div className='text-gray-700 fs-7'>
              Use 8 or more characters with a mix of letters, numbers & symbols.
            </div>
          </div>
        )
      case 3:
        return (
          <div className='d-flex flex-column justify-content-center gap-4'>
            {formik.status && (
              <div className='error-message alert alert-danger'>
                <div className='alert-text font-weight-bold'>{formik.status}</div>
              </div>
            )}

            <div className='d-flex justify-content-between gap-4'>
              <div className='flex-grow-1'>
                <label className='form-label fw-semibold text-dark fs-6'>First name</label>
                <input
                  placeholder='First name'
                  type='text'
                  autoComplete='off'
                  {...formik.getFieldProps('firstname')}
                  className={clsx(
                    'form-control form-control-sm bg-transparent',
                    {
                      'is-invalid': formik.touched.firstname && formik.errors.firstname,
                    },
                    {
                      'is-valid': formik.touched.firstname && !formik.errors.firstname,
                    }
                  )}
                />
                {formik.touched.firstname && formik.errors.firstname && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.firstname}</span>
                    </div>
                  </div>
                )}
              </div>

              <div className='flex-grow-1'>
                <label className='form-label fw-semibold text-dark fs-6'>Last name</label>
                <input
                  placeholder='Last name'
                  type='text'
                  autoComplete='off'
                  {...formik.getFieldProps('lastname')}
                  className={clsx(
                    'form-control form-control-sm bg-transparent',
                    {
                      'is-invalid': formik.touched.lastname && formik.errors.lastname,
                    },
                    {
                      'is-valid': formik.touched.lastname && !formik.errors.lastname,
                    }
                  )}
                />
                {formik.touched.lastname && formik.errors.lastname && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.lastname}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )
    }
  }

  const handleContinue = () => {
    if (currentStep === 1) {
      if (formik.values.email) {
        posthog.capture('trust_center_registration_email_entered', {
          email: formik.values.email,
        })
      }
    }

    if (currentStep === 3) {
      formik.submitForm()
      return
    }
    setCurrentStep((prev) => prev + 1)
  }

  const isContinueDisabled = (): boolean => {
    switch (currentStep) {
      case 1:
        return Boolean(!formik.values.email || formik.errors.email)
      case 2:
        return Boolean(!formik.values.password || formik.errors.password)
      case 3:
        return Boolean(
          !formik.values.firstname ||
            !formik.values.lastname ||
            formik.errors.firstname ||
            formik.errors.lastname
        )
      default:
        return false
    }
  }

  return (
    <>
      {' '}
      <Helmet>
        <title>SecureSlate</title>
      </Helmet>
      <div className='d-flex flex-column flex-lg-row flex-column-fluid h-100'>
        {/* begin::Body */}
        <div
          className='d-flex flex-column flex-lg-row-fluid w-lg-50 p-5 order-2 order-lg-1 '
          style={{maxHeight: '100vh', overflowY: 'auto'}}
        >
          {/* begin::Form */}
          <div className='d-flex flex-center flex-column flex-lg-row-fluid'>
            {/* begin::Wrapper */}
            <div className='w-lg-600px '>
              <form
                className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
                noValidate
                id='kt_login_signup_form'
                onSubmit={formik.handleSubmit}
              >
                {/* begin::Heading */}
                <div className='p-lg-5 my-8'>
                  <div className='d-inline-flex bg-gray-200 p-2 rounded-2 align-self-start mb-12'>
                    <img
                      alt='Logo'
                      src={toAbsoluteUrl('/Secureslate_logos/Secureslate_dark.png')}
                      className='h-20px app-sidebar-logo-default'
                    />
                  </div>

                  {renderStep()}

                  <div className='text-center mt-4'>
                    <button
                      type='submit'
                      id='kt_sign_up_submit'
                      className='btn btn-lg btn-sm btn-primary w-100 mb-4 fs-6'
                      onClick={(e) => {
                        e.preventDefault()
                        handleContinue()
                      }}
                      disabled={isContinueDisabled() || loading}
                    >
                      {currentStep === 3 ? (
                        loading ? (
                          <>
                            <span
                              className='spinner-border spinner-border-sm me-2'
                              role='status'
                              aria-hidden='true'
                            ></span>
                            Getting Started...
                          </>
                        ) : (
                          'Get Started'
                        )
                      ) : (
                        <span className=''>Continue</span>
                      )}
                    </button>
                    {currentStep === 1 && (
                      <span className='fs-6 fw-medium text-gray-700'>
                        Already have an account?
                        <Link to='/auth/login'>{''} Login</Link>
                      </span>
                    )}
                  </div>
                </div>
              </form>
            </div>

            {/* end::Wrapper */}
          </div>
          {/* end::Form */}
          {/* begin::Footer */}
          <div className='d-flex flex-center flex-wrap pt-10 '>
            {/* begin::Links */}
            <div className='d-flex fw-semibold text-primary fs-base'>
              <a
                href='https://getsecureslate.com/policy#terms'
                className='px-5'
                target='_blank'
                rel='noreferrer'
                style={{color: '#31bd93'}}
              >
                Terms
              </a>

              <a
                href='https://getsecureslate.com/pricing'
                className='px-5'
                target='_blank'
                rel='noreferrer'
                style={{color: '#31bd93'}}
              >
                Plans
              </a>

              <a
                href='https://getsecureslate.com/contact'
                className='px-5'
                target='_blank'
                rel='noreferrer'
                style={{color: '#31bd93'}}
              >
                Contact Us
              </a>
            </div>
            {/* end::Links */}
          </div>
          {/* end::Footer */}
        </div>
        {/* end::Body */}

        {/* begin::Aside */}
        <div
          className='d-flex flex-lg-row-fluid w-lg-50 bgi-size-contain bgi-position-center order-1 order-lg-2'
          style={{backgroundColor: '#1f1f1f'}}
        >
          {/* begin::Content */}
          <div className='d-flex flex-column flex-center py-7 py-lg-15 px-5 px-md-15 w-100'>
            {/* begin::Logo */}
            <Link to='/' className='mb-0 mt-6 mb-lg-12'>
              <img
                alt='Logo'
                src={toAbsoluteUrl('/Secureslate_logos/logo.png')}
                className='h-50px'
              />
            </Link>
            {/* end::Logo */}
            {/* begin::Image */}
            <img
              className='mx-auto w-275px w-md-50 w-xl-500px mb-10 mb-lg-20 d-none d-sm-block'
              src={toAbsoluteUrl('/media/svg/illustrations/iso-27001-headerimage.svg')}
              alt=''
            />
            {/* end::Image */}-{/* begin::Title */}
            <h3 className='text-white fs-2qx fw-bolder text-center mb-7 d-none d-sm-block'>
              The world's most cutting-edge platform for automating compliance
            </h3>
            {/* end::Title */}
            {/* begin::Text */}
            <div className='fs-lg-3 text-gray-400 d-none d-sm-block'>
              ISO 27001, SOC 2, PCI DSS, HIPAA, GDPR, CCPA, and more.
            </div>
            {/* end::Text */}
          </div>
          {/* end::Content */}
        </div>
        {/* end::Aside */}
      </div>
    </>
  )
}
